import { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';

const PATHWAYS = [
  {
    id: 'factory',
    label: 'Purchase finished knobs',
    title: 'Factory-Installed RangeSafe Knobs',
    description:
      'Purchase RangeSafe knobs for installation on compatible appliances during manufacturing.',
    video: '/assets/manufacturers/factory-installed.mp4',
    poster: '/assets/manufacturers/factory-installed-poster.jpg',
    points: [
      'Source finished RangeSafe knobs',
      'Assess fit and compatibility with your appliance platform',
      'Install the knobs during appliance assembly',
    ],
    cta: { label: 'Discuss Sourcing Our Knobs', to: '/manufacturers?interest=factory#manufacturers-form' },
    testid: 'pathway-factory',
  },
  {
    id: 'licensing',
    label: 'Integrate technology into your design',
    title: 'License RangeSafe Technology',
    description:
      'License RangeSafe technology for integration into your own knob designs and manufacturing process.',
    video: '/assets/manufacturers/licensing.mp4',
    poster: '/assets/manufacturers/licensing-poster.jpg',
    points: [
      'Explore technology licensing terms',
      'Evaluate integration with your own knob designs',
      'Incorporate the licensed technology during manufacturing',
    ],
    cta: { label: 'Explore Licensing Our Technology', to: '/manufacturers?interest=licensing#manufacturers-form' },
    testid: 'pathway-licensing',
  },
];

const EASE = [0.4, 0, 0.2, 1];

export default function ManufacturerPathways() {
  const containerRef = useRef(null);
  const videoRefs = useRef([]);
  const inView = useInView(containerRef, { once: true, amount: 0.3 });

  // Start every pathway video at the same moment once the section is in view and
  // all clips are buffered enough to play — so they are synchronized, not staggered.
  useEffect(() => {
    if (!inView) return undefined;
    const vids = videoRefs.current.filter(Boolean);
    if (vids.length === 0) return undefined;
    let done = false;

    const startAll = () => {
      if (done) return;
      done = true;
      vids.forEach((v) => {
        try {
          v.currentTime = 0;
          const pr = v.play();
          if (pr && typeof pr.catch === 'function') pr.catch(() => {});
        } catch (e) {
          /* ignore */
        }
      });
    };

    const tryStart = () => {
      if (vids.every((v) => v.readyState >= 3)) startAll();
    };

    vids.forEach((v) => {
      v.addEventListener('canplaythrough', tryStart);
      v.addEventListener('canplay', tryStart);
      v.addEventListener('loadeddata', tryStart);
      try {
        v.load();
      } catch (e) {
        /* ignore */
      }
    });
    tryStart();
    // Fallback: don't wait forever if one clip buffers slowly.
    const fb = setTimeout(startAll, 5000);

    return () => {
      done = true;
      clearTimeout(fb);
      vids.forEach((v) => {
        v.removeEventListener('canplaythrough', tryStart);
        v.removeEventListener('canplay', tryStart);
        v.removeEventListener('loadeddata', tryStart);
      });
    };
  }, [inView]);

  return (
    <div ref={containerRef} className="grid gap-px border border-line bg-line lg:grid-cols-2" data-testid="manufacturer-pathways">
      {PATHWAYS.map((p, i) => (
        <motion.div
          key={p.id}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="flex h-full flex-col bg-base p-6 sm:p-7 lg:p-8"
          data-testid={p.testid}
        >
          {/* video on top */}
          <div className="relative aspect-[3/2] w-full overflow-hidden rounded-sm border border-line2 bg-black">
            <video
              ref={(el) => {
                videoRefs.current[i] = el;
              }}
              className="h-full w-full object-cover"
              src={p.video}
              poster={p.poster || undefined}
              muted
              loop
              playsInline
              preload="auto"
              aria-label={`${p.title} — illustrative film`}
              data-testid={`${p.id}-video`}
            />
          </div>

          {/* details underneath */}
          <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.3em] text-accentText">{p.label}</p>
          <h3 className="mt-4 font-display text-2xl font-bold uppercase leading-[1.02] tracking-tight text-white sm:text-3xl">
            {p.title}
          </h3>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-dim">{p.description}</p>

          <ul className="mt-8 space-y-4 border-t border-line2 pt-6 text-sm leading-relaxed text-dim">
            {p.points.map((pt) => (
              <li key={pt} className="flex gap-4">
                <span className="mt-2 h-1 w-1 shrink-0 bg-accent" aria-hidden="true" />
                {pt}
              </li>
            ))}
          </ul>

          <div className="mt-auto pt-8">
            <Link
              to={p.cta.to}
              className="inline-flex h-12 w-full items-center justify-center gap-2 whitespace-nowrap border border-accent bg-accent px-3 font-mono text-[9px] font-medium uppercase tracking-[0.12em] text-white transition-colors duration-200 hover:bg-accent2 hover:border-accent2 sm:px-7 sm:text-[11px] sm:tracking-[0.22em]"
              data-testid={`${p.id}-cta`}
            >
              {p.cta.label}
            </Link>
          </div>
        </motion.div>
      ))}
    </div>
  );
}
